import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import {
  Settings,
  Add,
  Edit,
  Delete,
  Refresh,
  Business,
  TrendingUp,
  Stars,
  AdminPanelSettings,
  RestartAlt,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { partnershipsService, PartnershipTypeDefinition, CommissionTier } from '@/services/partnershipsService';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const PartnershipSettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // Partnership Types state
  const [partnershipTypes, setPartnershipTypes] = useState<PartnershipTypeDefinition[]>([]);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState<PartnershipTypeDefinition | null>(null);
  const [typeFormData, setTypeFormData] = useState({
    typeCode: '',
    displayName: '',
    description: '',
    baseCommissionRate: 5,
    commissionModel: 'flat' as 'flat' | 'tiered',
    isActive: true,
  });
  const [typeActionLoading, setTypeActionLoading] = useState(false);

  // Commission Tiers state
  const [commissionTiers, setCommissionTiers] = useState<CommissionTier[]>([]);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<CommissionTier | null>(null);
  const [tierFormData, setTierFormData] = useState({
    partnershipTypeId: '',
    name: '',
    displayName: '',
    tierLevel: 1,
    minVolume: 0,
    maxVolume: null as number | null,
    commissionRate: 5,
    benefits: {} as Record<string, any>,
  });
  const [tierActionLoading, setTierActionLoading] = useState(false);
  const [tierError, setTierError] = useState<string | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    severity: 'warning' as 'warning' | 'error' | 'info' | 'question',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadPartnershipTypes(), loadCommissionTiers()]);
    setLoading(false);
  };

  // Partnership Types Methods
  const loadPartnershipTypes = async () => {
    try {
      const response = await partnershipsService.getPartnershipTypes();
      if (response.success && response.data) {
        setPartnershipTypes(response.data.partnershipTypes || []);
      }
    } catch (error) {
      console.error('Failed to load partnership types:', error);
    }
  };

  const handleOpenTypeDialog = (type?: PartnershipTypeDefinition) => {
    if (type) {
      setEditingType(type);
      setTypeFormData({
        typeCode: type.typeCode,
        displayName: type.displayName,
        description: type.description || '',
        baseCommissionRate: type.baseCommissionRate,
        commissionModel: type.commissionModel || 'flat',
        isActive: type.isActive,
      });
    } else {
      setEditingType(null);
      setTypeFormData({
        typeCode: '',
        displayName: '',
        description: '',
        baseCommissionRate: 5,
        commissionModel: 'flat',
        isActive: true,
      });
    }
    setTypeDialogOpen(true);
  };

  const handleSaveType = async () => {
    setTypeActionLoading(true);
    try {
      let response;
      if (editingType) {
        // Update existing type
        response = await partnershipsService.updatePartnershipType(editingType.id, {
          displayName: typeFormData.displayName,
          description: typeFormData.description,
          baseCommissionRate: typeFormData.baseCommissionRate,
        });
      } else {
        // Create new type
        response = await partnershipsService.createPartnershipType(typeFormData);
      }

      if (response.success) {
        toast.success(editingType ? 'Partnership type updated' : 'Partnership type created');
        setTypeDialogOpen(false);
        await loadPartnershipTypes();
      } else {
        toast.error(response.error || 'Failed to save partnership type');
      }
    } catch (error) {
      toast.error('Failed to save partnership type');
    } finally {
      setTypeActionLoading(false);
    }
  };

  const handleDeleteType = async (typeId: string) => {
    const typeToDelete = partnershipTypes.find(t => t.id === typeId);

    setConfirmDialog({
      open: true,
      title: 'Delete Partnership Type',
      message: typeToDelete
        ? `Are you sure you want to delete "${typeToDelete.displayName}" partnership type? This action cannot be undone.`
        : 'Are you sure you want to delete this partnership type? This cannot be undone.',
      severity: 'error',
      onConfirm: async () => {
        try {
          const response = await partnershipsService.deletePartnershipType(typeId);
          if (response.success) {
            toast.success('Partnership type deleted');
            await loadPartnershipTypes();
          } else {
            toast.error(response.error || 'Failed to delete partnership type');
          }
        } catch (error) {
          toast.error('Failed to delete partnership type');
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      },
    });
  };

  // Commission Tiers Methods
  const loadCommissionTiers = async () => {
    try {
      const response = await partnershipsService.getCommissionTiers();
      if (response.success && response.data) {
        setCommissionTiers(response.data.tiers || []);
      }
    } catch (error) {
      console.error('Failed to load commission tiers:', error);
    }
  };

  const handleOpenTierDialog = (tier?: CommissionTier) => {
    setTierError(null); // Clear any previous errors
    if (tier) {
      setEditingTier(tier);
      setTierFormData({
        partnershipTypeId: tier.partnershipTypeId,
        name: tier.tierName,
        displayName: tier.displayName,
        tierLevel: tier.tierLevel,
        minVolume: tier.minVolume,
        maxVolume: tier.maxVolume,
        commissionRate: tier.commissionRate,
        benefits: tier.benefits || {},
      });
    } else {
      setEditingTier(null);
      setTierFormData({
        partnershipTypeId: '',
        name: '',
        displayName: '',
        tierLevel: 1,
        minVolume: 0,
        maxVolume: null,
        commissionRate: 5,
        benefits: {},
      });
    }
    setTierDialogOpen(true);
  };

  const handleSaveTier = async () => {
    setTierActionLoading(true);
    setTierError(null); // Clear previous errors
    try {
      // Map form data to API format
      const tierData = {
        partnershipTypeId: tierFormData.partnershipTypeId,
        tierName: tierFormData.name,
        displayName: tierFormData.displayName,
        tierLevel: tierFormData.tierLevel,
        minVolume: tierFormData.minVolume,
        maxVolume: tierFormData.maxVolume,
        commissionRate: tierFormData.commissionRate,
        benefits: tierFormData.benefits,
      };

      if (editingTier) {
        const response = await partnershipsService.updateCommissionTier(editingTier.id, tierData);
        if (response.success) {
          toast.success('Commission tier updated');
          setTierDialogOpen(false);
          await loadCommissionTiers();
        } else {
          setTierError(response.error || 'Failed to update commission tier');
        }
      } else {
        const response = await partnershipsService.createCommissionTier(tierData);
        if (response.success) {
          toast.success('Commission tier created');
          setTierDialogOpen(false);
          await loadCommissionTiers();
        } else {
          // Check if it's a duplicate tier level error
          const errorMessage = response.error || '';
          const errorDetails = response.details || '';
          if (errorMessage.includes('duplicate') || errorMessage.includes('unique constraint') ||
              errorDetails.includes('duplicate') || errorDetails.includes('unique_tier_level_per_type')) {
            const partnershipType = partnershipTypes.find(pt => pt.id === tierFormData.partnershipTypeId);
            setTierError(
              `Partnership type "${partnershipType?.displayName || 'Unknown'}" already has a tier at level ${tierFormData.tierLevel}. Please use a different tier level.`
            );
          } else {
            setTierError(response.error || 'Failed to create commission tier');
          }
        }
      }
    } catch (error) {
      setTierError('Failed to save commission tier');
    } finally {
      setTierActionLoading(false);
    }
  };

  const handleDeleteTier = async (tierId: string) => {
    const tierToDelete = commissionTiers.find(t => t.id === tierId);
    const partnershipType = tierToDelete
      ? partnershipTypes.find(pt => pt.id === tierToDelete.partnershipTypeId)
      : null;

    setConfirmDialog({
      open: true,
      title: 'Delete Commission Tier',
      message: tierToDelete && partnershipType
        ? `Are you sure you want to delete "${tierToDelete.displayName}" (Level ${tierToDelete.tierLevel}) from ${partnershipType.displayName} partnership type? This action cannot be undone.`
        : 'Are you sure you want to delete this commission tier? This cannot be undone.',
      severity: 'error',
      onConfirm: async () => {
        try {
          const response = await partnershipsService.deleteCommissionTier(tierId);
          if (response.success) {
            toast.success('Commission tier deleted');
            await loadCommissionTiers();
          } else {
            toast.error(response.error || 'Failed to delete commission tier');
          }
        } catch (error) {
          toast.error('Failed to delete commission tier');
        } finally {
          setConfirmDialog(prev => ({ ...prev, open: false }));
        }
      },
    });
  };

  const getTierColor = (tierName: string) => {
    switch (tierName.toLowerCase()) {
      case 'bronze':
        return '#CD7F32';
      case 'silver':
        return '#C0C0C0';
      case 'gold':
        return '#FFD700';
      case 'platinum':
        return '#E5E4E2';
      default:
        return '#666';
    }
  };

  const formatVolume = (volume: number | null) => {
    if (volume === null) return '∞';
    return volume.toLocaleString();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Settings sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Partnership Settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage partnership types and commission tier structure
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Partnership Types" icon={<Business />} iconPosition="start" />
          <Tab label="Commission Tiers" icon={<TrendingUp />} iconPosition="start" />
          <Tab label="Administrative Operations" icon={<AdminPanelSettings />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Partnership Types Tab */}
      <TabPanel value={activeTab} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Partnership Types</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenTypeDialog()}
          >
            Add Partnership Type
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Base Commission Rate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {partnershipTypes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" py={4}>
                        No partnership types found. Create one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  partnershipTypes.map((type) => (
                    <TableRow key={type.id} hover>
                      <TableCell>
                        <Chip label={type.typeCode} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {type.displayName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {type.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" color="primary">
                          {type.baseCommissionRate}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={type.isActive ? 'Active' : 'Inactive'}
                          color={type.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenTypeDialog(type)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteType(type.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Commission Tiers Tab */}
      <TabPanel value={activeTab} index={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Commission Tier Structure</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenTierDialog()}
          >
            Add Commission Tier
          </Button>
        </Box>

        {/* Visual Tier Progression */}
        {!loading && commissionTiers.length > 0 && (
          <Grid container spacing={2} mb={4}>
            {commissionTiers
              .sort((a, b) => a.tierLevel - b.tierLevel)
              .map((tier) => (
                <Grid item xs={12} sm={6} md={3} key={tier.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: getTierColor(tier.tierName),
                      borderWidth: 2,
                      height: '100%',
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <Stars sx={{ color: getTierColor(tier.tierName) }} />
                        <Typography
                          variant="h6"
                          fontWeight="bold"
                          sx={{ color: getTierColor(tier.tierName) }}
                        >
                          {tier.displayName}
                        </Typography>
                      </Box>
                      <Typography variant="h4" fontWeight="bold" color="primary" mb={1}>
                        {tier.commissionRate}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Volume Range
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ${formatVolume(tier.minVolume)} - ${formatVolume(tier.maxVolume)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tier</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell>Min Volume</TableCell>
                  <TableCell>Max Volume</TableCell>
                  <TableCell>Commission Rate</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commissionTiers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary" py={4}>
                        No commission tiers found. Create one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  commissionTiers
                    .sort((a, b) => a.tierLevel - b.tierLevel)
                    .map((tier) => (
                      <TableRow key={tier.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Stars sx={{ color: getTierColor(tier.tierName), fontSize: 20 }} />
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ color: getTierColor(tier.tierName) }}
                            >
                              {tier.displayName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={`Level ${tier.tierLevel}`} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            ${formatVolume(tier.minVolume)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            ${formatVolume(tier.maxVolume)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {tier.commissionRate}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenTierDialog(tier)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteTier(tier.id)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Administrative Operations Tab */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="h6" gutterBottom>
          Volume Reset Operations
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Reset Year-to-Date (YTD) or Month-to-Date (MTD) booking volumes and commission earnings for all partnerships or a specific partnership.
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Yearly Reset Card */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <RestartAlt color="primary" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">Yearly (YTD) Reset</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Reset Year-to-Date booking volumes and commission earnings to zero. This operation is typically run on January 1st for annual tier competition.
                </Typography>
                <Typography variant="caption" display="block" color="warning.main" sx={{ mb: 2 }}>
                  ⚠️ This action will reset ytd_booking_volume and ytd_commission_earned to 0 for all partnerships.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<RestartAlt />}
                  onClick={() => {
                    setConfirmDialog({
                      open: true,
                      title: 'Reset Yearly (YTD) Volumes',
                      message: 'Are you sure you want to reset Year-to-Date volumes for ALL partnerships? This will set ytd_booking_volume and ytd_commission_earned to 0. This action cannot be undone.',
                      severity: 'warning',
                      onConfirm: async () => {
                        try {
                          const response = await partnershipsService.resetPartnershipVolumes({
                            resetType: 'yearly',
                            confirm: true,
                          });
                          if (response.success) {
                            toast.success(response.message || 'YTD volumes reset successfully');
                          } else {
                            toast.error(response.error || 'Failed to reset YTD volumes');
                          }
                        } catch (error) {
                          toast.error('Failed to reset YTD volumes');
                        } finally {
                          setConfirmDialog(prev => ({ ...prev, open: false }));
                        }
                      },
                    });
                  }}
                >
                  Reset Yearly (YTD) Volumes
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Monthly Reset Card */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <RestartAlt color="secondary" sx={{ fontSize: 32 }} />
                  <Typography variant="h6">Monthly (MTD) Reset</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Reset Month-to-Date booking volumes and commission earnings to zero. This operation is typically run on the 1st of each month for monthly performance tracking.
                </Typography>
                <Typography variant="caption" display="block" color="warning.main" sx={{ mb: 2 }}>
                  ⚠️ This action will reset mtd_booking_volume and mtd_commission_earned to 0 for all partnerships.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  startIcon={<RestartAlt />}
                  onClick={() => {
                    setConfirmDialog({
                      open: true,
                      title: 'Reset Monthly (MTD) Volumes',
                      message: 'Are you sure you want to reset Month-to-Date volumes for ALL partnerships? This will set mtd_booking_volume and mtd_commission_earned to 0. This action cannot be undone.',
                      severity: 'warning',
                      onConfirm: async () => {
                        try {
                          const response = await partnershipsService.resetPartnershipVolumes({
                            resetType: 'monthly',
                            confirm: true,
                          });
                          if (response.success) {
                            toast.success(response.message || 'MTD volumes reset successfully');
                          } else {
                            toast.error(response.error || 'Failed to reset MTD volumes');
                          }
                        } catch (error) {
                          toast.error('Failed to reset MTD volumes');
                        } finally {
                          setConfirmDialog(prev => ({ ...prev, open: false }));
                        }
                      },
                    });
                  }}
                >
                  Reset Monthly (MTD) Volumes
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Important Notes:
          </Typography>
          <Typography variant="caption" component="div">
            • These operations affect ALL active partnerships
          </Typography>
          <Typography variant="caption" component="div">
            • All resets are logged in the admin audit logs
          </Typography>
          <Typography variant="caption" component="div">
            • Previous values are recorded before reset
          </Typography>
          <Typography variant="caption" component="div">
            • This action cannot be reversed
          </Typography>
        </Alert>
      </TabPanel>

      {/* Partnership Type Dialog */}
      <Dialog open={typeDialogOpen} onClose={() => setTypeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingType ? 'Edit Partnership Type' : 'Add Partnership Type'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Code"
              value={typeFormData.typeCode}
              onChange={(e) => setTypeFormData({ ...typeFormData, typeCode: e.target.value })}
              placeholder="e.g., HOTEL, EVENT_PLANNER"
              helperText="Unique identifier for this partnership type"
              disabled={!!editingType}
              required
            />
            <TextField
              label="Name"
              value={typeFormData.displayName}
              onChange={(e) => setTypeFormData({ ...typeFormData, displayName: e.target.value })}
              placeholder="e.g., Hotel Partnership"
              required
            />
            <TextField
              label="Description"
              value={typeFormData.description}
              onChange={(e) => setTypeFormData({ ...typeFormData, description: e.target.value })}
              multiline
              rows={3}
              placeholder="Brief description of this partnership type..."
            />
            <TextField
              label="Base Commission Rate (%)"
              type="number"
              value={typeFormData.baseCommissionRate}
              onChange={(e) =>
                setTypeFormData({ ...typeFormData, baseCommissionRate: parseFloat(e.target.value) || 0 })
              }
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              required
            />
            <TextField
              select
              label="Commission Model"
              value={typeFormData.commissionModel}
              onChange={(e) =>
                setTypeFormData({ ...typeFormData, commissionModel: e.target.value as 'flat' | 'tiered' | 'flat_with_bonuses' })
              }
              required
              disabled={!!editingType}
              helperText={editingType ? "Commission model cannot be changed after creation" : "Flat: Fixed rate | Tiered: Volume-based | Flat with Bonuses: Fixed rate + performance bonuses"}
            >
              <MenuItem value="flat">Flat Rate</MenuItem>
              <MenuItem value="tiered">Tiered (Volume-based)</MenuItem>
              <MenuItem value="flat_with_bonuses">Flat with Bonuses</MenuItem>
            </TextField>
            <TextField
              select
              label="Status"
              value={typeFormData.isActive ? 'active' : 'inactive'}
              onChange={(e) =>
                setTypeFormData({ ...typeFormData, isActive: e.target.value === 'active' })
              }
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTypeDialogOpen(false)} disabled={typeActionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveType}
            variant="contained"
            disabled={typeActionLoading || !typeFormData.typeCode || !typeFormData.displayName}
          >
            {typeActionLoading ? <CircularProgress size={24} /> : editingType ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commission Tier Dialog */}
      <Dialog open={tierDialogOpen} onClose={() => setTierDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTier ? 'Edit Commission Tier' : 'Add Commission Tier'}</DialogTitle>
        <DialogContent>
          {tierError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTierError(null)}>
              {tierError}
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              select
              label="Partnership Type"
              value={tierFormData.partnershipTypeId}
              onChange={(e) => setTierFormData({ ...tierFormData, partnershipTypeId: e.target.value })}
              required
              disabled={!!editingTier}
              helperText={editingTier ? "Partnership type cannot be changed after creation" : "Select which partnership type this tier belongs to"}
            >
              {partnershipTypes
                .filter(type => type.commissionModel === 'tiered')
                .map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.displayName} ({type.typeCode})
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              label="Tier Name"
              value={tierFormData.name}
              onChange={(e) => setTierFormData({ ...tierFormData, name: e.target.value.toLowerCase() })}
              placeholder="e.g., bronze, starter, level_1"
              helperText="Internal identifier (lowercase, max 50 chars). Examples: bronze, starter, beginner, explorer"
              required
              disabled={!!editingTier}
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Display Name"
              value={tierFormData.displayName}
              onChange={(e) => setTierFormData({ ...tierFormData, displayName: e.target.value })}
              placeholder="e.g., Bronze Partner"
              required
            />
            <TextField
              label="Tier Level"
              type="number"
              value={tierFormData.tierLevel}
              onChange={(e) =>
                setTierFormData({ ...tierFormData, tierLevel: parseInt(e.target.value) || 1 })
              }
              inputProps={{ min: 1, max: 10, step: 1 }}
              helperText="Order of this tier in the progression (1 = lowest)"
              required
            />
            <TextField
              label="Minimum Volume ($)"
              type="number"
              value={tierFormData.minVolume}
              onChange={(e) =>
                setTierFormData({ ...tierFormData, minVolume: parseFloat(e.target.value) || 0 })
              }
              inputProps={{ min: 0, step: 1000 }}
              required
            />
            <TextField
              label="Maximum Volume ($)"
              type="number"
              value={tierFormData.maxVolume || ''}
              onChange={(e) =>
                setTierFormData({
                  ...tierFormData,
                  maxVolume: e.target.value ? (parseFloat(e.target.value) || null) : null,
                })
              }
              inputProps={{ min: 0, step: 1000 }}
              helperText="Leave empty for no upper limit (e.g., Platinum tier)"
            />
            <TextField
              label="Commission Rate (%)"
              type="number"
              value={tierFormData.commissionRate}
              onChange={(e) =>
                setTierFormData({ ...tierFormData, commissionRate: parseFloat(e.target.value) || 0 })
              }
              inputProps={{ min: 0, max: 100, step: 0.1 }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTierDialogOpen(false)} disabled={tierActionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTier}
            variant="contained"
            disabled={
              tierActionLoading || !tierFormData.partnershipTypeId || !tierFormData.name || !tierFormData.displayName || tierFormData.tierLevel < 1
            }
          >
            {tierActionLoading ? <CircularProgress size={24} /> : editingTier ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Container>
  );
};

export default PartnershipSettingsPage;
